import { useCallback, useMemo, useState, useRef } from 'react';
import MojitoCore from 'mojito/core';
import { connect } from 'react-redux';
import {
    selectTopSportLive,
    selectTopSportFeatured,
    selectTopSportLiveAndFeatured,
} from './store/selectors';
import { actions as topSportsActions } from './store/slice.js';
import MojitoPresentation from 'mojito/presentation';
import TopSportsView from './top-sports-view/index.js';
import TopSportsTypes from './types.js';
import { useSortedItems, useSportMetaInfo, useSportMetasLoadDone } from 'modules/common/hooks';
import { useAwaitingClientsLoadDone, useAwaitingClientsRegistry } from 'modules/performance/hooks';

const { CONTENT_TYPE } = TopSportsTypes;

const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;
const AnalyticsContextExtender = MojitoCore.Presentation.AnalyticsContext.ContextExtender;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const { PostponedRenderer } = MojitoPresentation.Components;

class TopSportsControllerView extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.analyticsContext = new AnalyticsContextBuilder()
            .withAnalyticsPath('TopSports')
            .withData({ listName: this.props.sportListName })
            .build();
        this.onSportClicked = this.onSportClicked.bind(this);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(this.props, nextProps) || !isShallowEqual(this.state, nextState);
    }

    onSportClicked(sportId) {
        const {
            mojitoTools: { config },
            dispatch,
        } = this.props;
        const { setTopSportLive, setTopSportFeatured, setTopSportLiveAndFeatured } =
            topSportsActions;

        this.emitAnalytics('topSportClicked', {
            sportListName: this.props.sportListName,
            sportType: sportId,
        });

        const action = resolveByContentType(
            config.view.contentType,
            setTopSportLive,
            setTopSportFeatured,
            setTopSportLiveAndFeatured
        );
        dispatch(action(sportId));
    }

    getSelectedSport(sports) {
        const {
            mojitoTools: { config },
            topSportLive,
            topSportFeatured,
            topSportLiveAndFeatured,
        } = this.props;
        const selectedSport = resolveByContentType(
            config.view.contentType,
            topSportLive,
            topSportFeatured,
            topSportLiveAndFeatured
        );
        return sports.some(sport => sport.id === selectedSport) ? selectedSport : sports[0]?.id;
    }

    render() {
        const { topSports, isLoadDone, navigationTitle, onShowUp } = this.props;
        const selectedSport = this.getSelectedSport(topSports);

        return (
            <AnalyticsContextExtender value={this.analyticsContext}>
                <TopSportsView
                    topSports={topSports}
                    isLoading={!isLoadDone}
                    navigationTitle={navigationTitle}
                    selectedSport={selectedSport}
                    onSportClicked={this.onSportClicked}
                    onShowUp={onShowUp}
                    config={this.config.view}
                />
            </AnalyticsContextExtender>
        );
    }
}

const TopSportsModule = props => {
    const {
        mojitoTools: {
            instanceId,
            config,
            appContext: { subscriptionResolver },
        },
    } = props;
    const sportListName = resolveSportListSubscription(
        config.view.contentType,
        subscriptionResolver
    );
    const { enableContentLazyLoading, reportLoadingStatus } = config;

    const [visible, setVisible] = useState(!enableContentLazyLoading);
    const sportListNames = useMemo(
        () => (visible ? [sportListName] : []),
        [sportListName, visible]
    );

    const topSportsInfo = useSportMetaInfo(visible ? sportListName : undefined);
    const topSports = useSortedItems(topSportsInfo, config.priorityType.value);

    const isLoadDone = useSportMetasLoadDone(sportListNames);
    const noContent = isLoadDone && topSports.length === 0;
    const syncAwaitingClients = useAwaitingClientsRegistry(
        'TopSports',
        isLoadDone,
        reportLoadingStatus ? instanceId : undefined
    );
    useAwaitingClientsLoadDone(isLoadDone, instanceId, noContent, 'TopSports');
    const onShowUp = useCallback(() => {
        setVisible(true);
        syncAwaitingClients();
    }, [syncAwaitingClients]);
    // Will disable postponed rendering if content is initially available as it gives unnecessary layout shift
    const initialDataRef = useRef(isLoadDone);

    if (isLoadDone && topSports.length === 0) {
        return null;
    }

    const disablePostponedRendering = !!initialDataRef.current || !config.postponedRendering;

    return (
        <PostponedRenderer disabled={disablePostponedRendering} name={'TopSports'}>
            <TopSportsControllerView
                {...props}
                sportListName={sportListName}
                topSports={topSports}
                onShowUp={onShowUp}
                isLoadDone={isLoadDone}
            />
        </PostponedRenderer>
    );
};

const resolveSportListSubscription = (contentType, subscriptionResolver) => {
    switch (contentType) {
        case CONTENT_TYPE.LIVE:
            return subscriptionResolver.sportsWithLiveMatchEvents();
        case CONTENT_TYPE.LIVE_AND_FUTURE:
            return subscriptionResolver.sportsWithLiveAndFutureMatchEvents();
        case CONTENT_TYPE.FUTURE:
        default:
            return subscriptionResolver.sportsWithFutureMatchEvents();
    }
};

const resolveByContentType = (contentType, live, future, liveAndFuture) => {
    switch (contentType) {
        case CONTENT_TYPE.LIVE:
            return live;
        case CONTENT_TYPE.LIVE_AND_FUTURE:
            return liveAndFuture;
        case CONTENT_TYPE.FUTURE:
        default:
            return future;
    }
};

const mapStateToProps = state => ({
    topSportLive: selectTopSportLive(state),
    topSportFeatured: selectTopSportFeatured(state),
    topSportLiveAndFeatured: selectTopSportLiveAndFeatured(state),
});

export default connect(mapStateToProps)(TopSportsModule);
